import React, {useState} from "react";
import {pageAuthorizedByGroup} from "../fragments/PageAuthorization";
import {GlobalState} from "../../global/GlobalState";
import {Link} from "react-router-dom";
import {
	Box,
	Breadcrumbs,
	CircularProgress, FormControl, InputLabel, MenuItem,
	Paper, Select, Stack, Typography
} from "@mui/material";

import {PentaSplit} from "../../components/common/PentaSplit";
import {graphQLServices} from "../../services/GraphQLServices";
import {translateFunction} from "../../localization/TranslationService";
import {CurrentStageDisplay, HeaderSlot} from "../../components/onsite/Footer";
import {GET_INCENTIVE_REGISTER, GET_INCENTIVE_REGISTER_NAME_OPTIONS} from "./Queries";
import {ApolloQueryResult, useQuery} from "@apollo/client";
import {download} from "../../components/common/DownloadButton";
import {IncentiveRegister} from "./models/IncentiveRegister";
import {DateTimePicker} from "../../components/common/input/DatePicker";
import {DateTimeFormatter, LocalDateTime} from "@js-joda/core";
import {ProcessButton} from "../../components/common/input/ProcessButton";
import {Download} from "@mui/icons-material";

const translate = translateFunction("SureBasePortalProductsCatalog");

export const IncentiveRegisterReport = pageAuthorizedByGroup(function () {
	return <>
		<HeaderSlot><CurrentStageDisplay/></HeaderSlot>
		<PentaSplit
			top={<Breadcrumbs style={{margin: "5px"}}>
				<Link style={{color: "inherit", textDecoration: "unset"}} to="/reports">{translate("Reports")}</Link>
				<Typography color="text.primary">{translate("Incentive register report")}</Typography>
			</Breadcrumbs>}
			center={<Paper style={{
				height: "100%",
			}}>
				<IncentiveRegisterReportContent/>
			</Paper>}
		/>
	</>;
}, ["Product"]);

export function IncentiveRegisterReportContent() {
	const [incentiveNames, setIncentiveNames] = useState<string[]>();
	const [selectedIncentiveName, setSelectedIncentiveName] = useState<string>();
	const [transactionStartDate, setTransactionStartDate] = useState<string | null>(null);
	const [transactionEndDate, setTransactionEndDate] = useState<string | null>(null);
	const getIncentiveRegisterNameOptionsQuery = useQuery(GET_INCENTIVE_REGISTER_NAME_OPTIONS, {
		client: graphQLServices.orderClient,
		fetchPolicy: "no-cache"
	});
	
	if (getIncentiveRegisterNameOptionsQuery.loading)
		return <Box sx={{display: "flex", width: "100%", height: "100%"}}>
			<CircularProgress style={{margin: "auto"}}/>
		</Box>;
	
	const error = getIncentiveRegisterNameOptionsQuery.error;
	if (error)
		throw error;
	
	if (!incentiveNames) {
		const incentiveNameOptions = getIncentiveRegisterNameOptionsQuery.data.distinctIncentiveNames.options;
		if (incentiveNameOptions === null)
			throw new Error(`Incentive names not found.`);
		
		setIncentiveNames(incentiveNameOptions);
	}
	
	function parseDateNullable(text: string | null): LocalDateTime | null {
		return text ? parseDate(text) : null;
	}
	
	function parseDate(text: string): LocalDateTime {
		return LocalDateTime.parse(text.endsWith("Z") ? text.substring(0, text.length - 1) : text);
	}
	
	return <Paper style={{
		height: "100%",
		width: "25%"
	}}>
		<Stack spacing={2}>
			<div></div>
			<FormControl>
				<DateTimePicker
					textFieldProps={{
						style: {width: "250px"}
					}}
					label={translate("Transaction Start Date")}
					variant={"standard"}
					value={parseDateNullable(transactionStartDate)}
					inputFormat={GlobalState.userPreferences.inputDateTimePatternOrDefault}
					onValueChange={value => {
						const valueString = value ? DateTimeFormatter.ISO_LOCAL_DATE_TIME.format(value) : null as unknown as string;
						setTransactionStartDate(valueString);
					}}
				/>
			</FormControl>
			<FormControl>
				<DateTimePicker
					textFieldProps={{
						style: {width: "250px"}
					}}
					label={translate("Transaction End Date")}
					variant={"standard"}
					value={parseDateNullable(transactionEndDate)}
					inputFormat={GlobalState.userPreferences.inputDateTimePatternOrDefault}
					onValueChange={value => {
						const valueString = value ? DateTimeFormatter.ISO_LOCAL_DATE_TIME.format(value) : null as unknown as string;
						setTransactionEndDate(valueString);
					}}
				/>
			</FormControl>
			<FormControl>
				<InputLabel id={"IncentiveNames"}>{translate("Incentive names")}</InputLabel>
				<Select
					style={{width: "250px"}}
					labelId={"IncentiveNames"}
					label={translate("Incentive names")}
					variant={"standard"}
					value={selectedIncentiveName ?? ""}
					onChange={event => setSelectedIncentiveName(event.target.value)}
				>
					<MenuItem key={"null"} value={""} hidden={true} style={{display: "none"}}></MenuItem>
					{(incentiveNames ?? []).map(it =>
						<MenuItem key={it.toLowerCase()} value={it}>{it}</MenuItem>)}
				</Select>
			</FormControl>
			{transactionStartDate && transactionEndDate && selectedIncentiveName && (<FormControl>
				<ProcessButton
					variant={"outlined"}
					startIcon={<Download/>}
					defaultText={"Download incentive register"}
					inProgressText={"Downloading incentive register"}
					completedText={"Downloaded incentive register"}
					onClick={() => downloadIncentiveRegister(transactionStartDate, transactionEndDate, selectedIncentiveName)}
				/>
			</FormControl>
			)}
		</Stack>
	</Paper>;
}


async function downloadIncentiveRegister(startDate: string, endDate: string, incentiveName: string) {
	const client = graphQLServices.orderClient;
	
	const nodes: IncentiveRegister[] = [];
	
	let cursor: string | null = null;
	let hasNextPage = false;
	do {
		const response: ApolloQueryResult<any> = await client.query({
			query: GET_INCENTIVE_REGISTER,
			variables: {
				startDate,
				endDate,
				incentiveName,
				after: cursor,
				pageSize: 5000
			}
		});
		
		nodes.push(...response.data.incentiveRegister.nodes);
		
		hasNextPage = response.data.incentiveRegister.pageInfo.hasNextPage ?? false;
		cursor = response.data.incentiveRegister.pageInfo.endCursor;
	} while (hasNextPage);
	
	const header = [
		"CreationDateTime",
		"IncentiveName",
		"Email",
		"Prefix",
		"FirstName",
		"Surname",
		"BirthDate",
		"HouseNumber",
		"HouseNumberAddition",
		"Postcode",
		"TransactionDate"
	];
	
	const rows = nodes
		.map(it => {
			return [
				it.creationDateTime ?? "",
				it.incentiveName ?? "",
				it.email ?? "",
				it.prefix ?? "",
				it.firstName ?? "",
				it.surname ?? "",
				it.birthDate ?? "",
				it.houseNumber ?? "",
				it.houseNumberAddition ?? "",
				it.postcode ?? "",
				it.transactionDate ?? ""
			];
		});
	
	const data = [header].concat(rows);
	const csv = toCsv(data);
	
	download(`export-incentive-register_${getDateTimeNow()}.csv`, csv, {
		data: "text/csv"
	});
}

function toCsv(data: string[][]): string {
	return data
		.map(row => row
			.map(cell =>
				cell.indexOf(",") >= 0 || cell.indexOf("\n") >= 0 || cell.indexOf("\"") >= 0
					? "\"" + cell.replaceAll("\"", "\"\"") + "\""
					: cell)
			.join(","))
		.join("\n");
}

function getDateTimeNow(): string {
	const now = new Date();
	
	const year = now.getFullYear();
	const month = String(now.getMonth() + 1).padStart(2, "0"); // Months are 0-based, so add 1
	const day = String(now.getDate()).padStart(2, "0");
	
	const hours = String(now.getHours()).padStart(2, "0");
	const minutes = String(now.getMinutes()).padStart(2, "0");
	const seconds = String(now.getSeconds()).padStart(2, "0");
	return `${year}_${month}_${day}_${hours}_${minutes}_${seconds}`;
}
